import { CircleAlert, Check, X, type LucideIcon } from 'lucide-react';
import type { ToastVariant } from '@/stores/toastStore';

export interface ToastProps {
  variant?: ToastVariant;
  message: string;
  /** Optional second line under the message. */
  description?: string;
  /** Optional text button (e.g. "Try again"); shown only with `onAction`. */
  actionLabel?: string;
  onAction?: () => void;
  onDismiss?: () => void;
}

const variantConfig: Record<
  ToastVariant,
  {
    icon: LucideIcon;
    iconClass: string;
    bgClass: string;
    borderClass: string;
    textClass: string;
  }
> = {
  default: {
    icon: Check,
    iconClass: 'text-muted-foreground',
    bgClass: 'bg-card',
    borderClass: 'border-border',
    textClass: 'text-muted-foreground',
  },
  // Figma's success toast draws a bare check, not a check in a circle
  // ("User profile updated", Settings / profile (changed) / success — QA on
  // YAR-142). The circled glyph is the error toast's shape language only.
  success: {
    icon: Check,
    iconClass: 'text-success',
    bgClass: 'bg-success-light',
    borderClass: 'border-success-border',
    textClass: 'text-success',
  },
  error: {
    icon: CircleAlert,
    iconClass: 'text-danger-hover',
    bgClass: 'bg-danger-light',
    borderClass: 'border-danger-border',
    textClass: 'text-danger-hover',
  },
  // Figma `.Alerts` information (Add to Yaruta "already added", .Toasts
  // 11697:17848): the error toast's circled "!" in blue. Informational,
  // not a problem, so never the red.
  info: {
    icon: CircleAlert,
    iconClass: 'text-info',
    bgClass: 'bg-info-light',
    borderClass: 'border-info-border',
    textClass: 'text-info',
  },
};

export function Toast({
  variant = 'default',
  message,
  description,
  actionLabel,
  onAction,
  onDismiss,
}: ToastProps) {
  const config = variantConfig[variant];
  const Icon = config.icon;

  return (
    <div
      role="alert"
      className={[
        'flex gap-3 rounded-md border px-3 py-3',
        // Two lines read from the top; one line stays centred.
        description ? 'items-start' : 'items-center',
        'w-[362px] max-w-[calc(100vw-32px)]',
        'shadow-[0px_0px_8px_-2px_rgba(56,64,74,0.08)]',
        config.bgClass,
        config.borderClass,
        config.textClass,
      ].join(' ')}
    >
      <Icon
        size={24}
        className={['shrink-0', config.iconClass].join(' ')}
      />
      {description ? (
        // Figma `.Alerts` (11697:17848): title body-md/medium, 6px above
        // the body.
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <p className="text-base font-medium leading-6">{message}</p>
          <p className="text-sm leading-5">{description}</p>
        </div>
      ) : (
        <p className="flex-1 text-base leading-6">{message}</p>
      )}
      {actionLabel && onAction && (
        <button
          type="button"
          onClick={onAction}
          className="shrink-0 cursor-pointer rounded px-1 text-base font-semibold leading-6 underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        >
          {actionLabel}
        </button>
      )}
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          // Figma: a 24px ✕ in icon/neutral/default on every variant, not
          // the variant's own colour.
          className="inline-flex shrink-0 cursor-pointer items-center justify-center rounded text-icon-default transition-colors duration-150 hover:text-text-heading focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          aria-label="Dismiss"
        >
          <X size={24} />
        </button>
      )}
    </div>
  );
}
